//
// SELECTORS, these are functions designed to work directly on and with the
// redux data store
//
import { createSelector } from 'reselect';
import createCachedSelector, { FlatMapCache, LruCacheObject } from 're-reselect';

import { List as ImmutableList, Map as ImmutableMap, Record } from 'immutable';
import { DateTime } from 'luxon';
import { getTags } from 'data/tags/selectors';
import { getGlobalCache, setGlobalCache } from 'utils/auth/caches';
import { getAccountsById, getAllAccountIds, getGoalAccountIds } from 'data/accounts/selectors';
import { createFlexEqualSelector } from 'utils/selectorsHelpers';
import { getCoaString } from 'data/categories/retrievers';
import { CoaTypeEnum } from 'data/chartOfAccounts/types';
import { getIncomeCOAIds } from 'data/categories/selectors';

import { parseDateString } from 'utils/utils';

import { REDUCER_KEY } from './reducer';
import {
  findTransaction,
  isAcceptedScheduledTxn,
  isGoalTxnInNonGoalAccount,
  isIncomeFromTxn,
  isUnacceptedScheduledTxn,
} from './utils';

export const getLoadPending = (state) => state[REDUCER_KEY].loadPending;
export const getError = (state) => state[REDUCER_KEY].error;
export const getIsLoading = (state) => state[REDUCER_KEY].isLoading;
export const getLastReductions = (state) => state[REDUCER_KEY].lastReductions;
export const getLastUpsertId = (state) => state[REDUCER_KEY].lastUpsertId;
export const getTransactionsStore = (state) => state[REDUCER_KEY];

export const TxnTypes = {
  GOALS: 1,
  REGULAR_TXNS: 2,
  UNACCEPTED_SCHEDULED_TXNS: 4,

  ALL: 0xFFFF,
};
TxnTypes.DEFAULT = TxnTypes.REGULAR_TXNS | TxnTypes.UNACCEPTED_SCHEDULED_TXNS;

// getTransactionsForAccountIdSelectors
//
// This selector returns a map of selectors that can then be used to get transactions
// filtered by txn-types for a specific account.
//
// We are using this level of indirection so that the returned transactions for each
// account will not be invalidated by the adding/removing of accounts or by changing
// transactional data in other accounts.
//
// The selectors returned by this method are not typical selectors. Instead of
// a state and props, the expected arguments are a map of transactions by
// account ID.
//
export const getTransactionsForAccountIdSelectors = createCachedSelector(
  getAllAccountIds,
  getGoalAccountIds,
  (_state, props) => (props && props.txnTypes) || TxnTypes.DEFAULT,
  (allAccountIds, goalAccountIds, txnTypes) =>
    ImmutableMap().withMutations((map) => {
      allAccountIds.forEach((accountId) => {
        const selector = createSelector(
          (transactionsByAccountId) => transactionsByAccountId.get(accountId),
          (txnsById) => {
            // handle special case of no transactions in account
            if (txnsById === undefined || txnsById.size === 0) {
              return ImmutableMap();
            }
            const filteredTxnsById = txnsById.filter((txn) => {
              if (isGoalTxnInNonGoalAccount(txn, goalAccountIds)) {
                return txnTypes & TxnTypes.GOALS;
              }
              if (isUnacceptedScheduledTxn(txn)) {
                return txnTypes & TxnTypes.UNACCEPTED_SCHEDULED_TXNS;
              }
              return txnTypes & TxnTypes.REGULAR_TXNS;
            });
            return filteredTxnsById;
          }
        );
        map.set(accountId, selector);
      });
    })
)({
  keySelector: (_state, props) => (props && props.txnTypes) || TxnTypes.DEFAULT,
  cacheObject: new FlatMapCache(),
});

export const getTransactionsByAccountId = createCachedSelector(
  getAllAccountIds,
  (state) => state[REDUCER_KEY].transactionsByAccountId,
  (state, props) => getTransactionsForAccountIdSelectors(state, props),
  (allAccountIds, txnsByAccountId, txnsForAccountIdSelectors) =>
    ImmutableMap().withMutations((map) => {
      allAccountIds.forEach((accountId) => {
        const txnsForAccountIdSelector = txnsForAccountIdSelectors.get(accountId);
        map.set(accountId, txnsForAccountIdSelector(txnsByAccountId));
      });
    })
)({
  keySelector: (_state, props) => (props && props.txnTypes) || TxnTypes.DEFAULT,
  cacheObject: new FlatMapCache(),
});

export const upcomingTransactions = createSelector(
  getTransactionsByAccountId,
  (state, props) => props,
  (transactionsByAccountId, { id, transaction: { accountId }}) => {
    const allTxn = transactionsByAccountId.get(accountId);
    return allTxn && allTxn
      .filter((d) => isUnacceptedScheduledTxn(d) && d.stModelId === id)
      .sort((a, b) => new Date(a.stDueOn) - new Date(b.stDueOn));
  }
);

export const pastTransactions = createSelector(
  getTransactionsByAccountId,
  (state, props) => props,
  (transactionsByAccountId, { id, transaction: { accountId } }) => {
    const allTxn = transactionsByAccountId.get(accountId);
    return allTxn && allTxn
      .filter((d) => isAcceptedScheduledTxn(d) && d.stModelId === id)
      .sort((a, b) => new Date(b.stDueOn) - new Date(a.stDueOn));
  }
);

export const getAllUpcomingTransactions = createSelector(
  getTransactionsByAccountId,
  (state, props) => props,
  (transactionsByAccountId, schTxns) => {
    if(!transactionsByAccountId || transactionsByAccountId.isEmpty()) {
      return new ImmutableMap();
    }

    return new ImmutableMap().withMutations((mutableMap) => {
      schTxns.forEach(({ id, transaction: { accountId }}) => {
        const allTxn = transactionsByAccountId.get(accountId);
        const allUpcomingTxn = allTxn && allTxn
          .filter((txn) => isUnacceptedScheduledTxn(txn) && txn.stModelId === id)
          .sort((a, b) => new Date(a.stDueOn) - new Date(b.stDueOn));

        if(allUpcomingTxn && !allUpcomingTxn.isEmpty()) {
          mutableMap.set(id, allUpcomingTxn);
        }
      });
    });
  }
);

export const getTransactionsById = createSelector(
  getTransactionsByAccountId,
  (transactionsByAccountId) => (new ImmutableMap()).withMutations(
    (flatTransactionsById) => transactionsByAccountId.forEach((transactionsById) => flatTransactionsById.merge(transactionsById))
  )
);

export const getTransactionsForAccountIds = createCachedSelector(
  (state, props) => props.accountIds,
  getTransactionsByAccountId,
  getAccountsById,
  (accountIds, transactionsByAccountId, accountsById) => {

    let accountIdsList = accountIds;
    if (!accountIds || accountIds.length === 0) {
      accountIdsList = accountsById.filter((x) => x.type !== 'GOAL').map((x) => x.id).toList();
    }

    let returnTransactions = new ImmutableMap();
    accountIdsList.forEach((id) => {
      returnTransactions = returnTransactions.merge(transactionsByAccountId.get(id))
    });

    return returnTransactions;

  }
)({
  keySelector: (state, props) => props.accountIds ? props.accountIds.hashCode() : 'all',
  cacheObject: new FlatMapCache(),
});


export const getTransactionById = (state, props) => getTransactionsById(state).get(props.id);

export const CategorySummary = Record({
  count: 0,
  amount: 0,
  amountByMonth: null,
  coa: null,
  acctId: null,
  monthlyAverage: null,
});

export const getMostPopularCatsByAccountId = createCachedSelector(
  getTransactionsByAccountId,
  (state, props) => (props && props.dateRange),
  (state, props) => (props && props.sortByAmount),
  (txns: any, dateRange: any, sortByAmount: any) => {

    let catCount = new ImmutableMap();

    txns.forEach((acctTxns, acctId) => {

      acctTxns.forEach((txn) => {

        if (txn.coa && txn.coa.type === CoaTypeEnum.CATEGORY) { // ignore splits, transfers, and uncategorized

          // check date if it is supplied
          if (!dateRange || dateRange.contains(DateTime.fromISO(txn.postedOn))) {
            const catRecord = catCount.get(txn.coa.id);
            let amount = +txn.amount;
            let count = 1;
            const { month } = DateTime.fromISO(txn.postedOn);
            let amountByMonth = new ImmutableMap().set(month, amount);

            if (catRecord) {
              amount = catRecord.amount + +txn.amount;
              count = catRecord.count + 1;
              amountByMonth = catRecord.amountByMonth.set(month, (catRecord.amountByMonth.get(month) || 0) + amount);
            }
            catCount = catCount.set(txn.coa.id, new CategorySummary({
              count,
              amount,
              amountByMonth,
              coa: txn.coa,
              acctId,
            }));
          }
        }
      });
    });
    if (sortByAmount) {
      return catCount.toList().sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    }
    return catCount.toList().sort((a, b) => b.count - a.count);
  }
)(
  (state, props, selectorKey) => ((props && props.dateRange && props.dateRange.toISO()) || 'nodate') +
    (props && props.sortByAmount) + selectorKey,
  { cacheObject: new LruCacheObject({ cacheSize: 5 }) },
);

export const getPopularCatsMonthlyAverage = createCachedSelector(
  (state, props) => getMostPopularCatsByAccountId(state, props),
  (popularCats) => {
    // must occur more than once
    let filteredCats = popularCats.filter((x) => x.amountByMonth.size > 1);

    // add in monthly average
    filteredCats = filteredCats.map((x) => x.set('monthlyAverage', (x.amount / x.amountByMonth.size).toFixed(0)));

    // sort by monthly average
    filteredCats = filteredCats.sort((a, b) => {

      if (a.amountByMonth.size === b.amountByMonth.size) {
        return b.monthlyAverage - a.monthlyAverage;
      }
      return ((b.amountByMonth.size * 5000) + Math.abs(b.amount)) - ((a.amountByMonth.size * 5000) + Math.abs(a.amount));
    });

    return filteredCats;
  }
)(
  (state, props, selectorKey) => ((props && props.dateRange && props.dateRange.toISO()) || 'nodate') +
    (props && props.sortByAmount) + selectorKey,
  { cacheObject: new LruCacheObject({ cacheSize: 5 }) },
);

export const getTransactionsByIds = createCachedSelector(
  getTransactionsById,
  (state, ids) => ids,
  (transactionsById, ids) => transactionsById && transactionsById.filter((transaction) => ids && ids.includes(transaction.id)),
)(
  (state, ids, selectorKey) => ids && ids.hashCode() + selectorKey,
  { cacheObject: new LruCacheObject({ cacheSize: 30 }) },
);

export const getTransactionsByFilterFn = createCachedSelector(
  (state, props ) => props && props.transactions || getTransactionsById(state),
  (state, props) => props,
  (transactionsById, props) => transactionsById.filter((transaction) => !props || !props.filterFn || props.filterFn(transaction, props)),
)(
  (state, props) => props && props.filterFnGUID,
  { cacheObject: new LruCacheObject({ cacheSize: 13 }) },
);

export const getTransactionsByFilter = createCachedSelector(
  (state, filter) => filter && filter.transactions || getTransactionsById(state),
  (_state, filter) => ({ obj: filter && filter.accountIds, deep: true }),
  (_state, filter) => ({ obj: filter && filter.accountIdsArray, deep: true }),
  (_state, filter) => ({ obj: filter && filter.startDate, deep: true }),
  (_state, filter) => ({ obj: filter && filter.endDate, deep: true }),
  (_state, filter) => ({ obj: filter && filter.payee, deep: false }),
  (_state, filter) => ({ obj: filter && filter.amountSign, deep: false }),
  (_state, filter) => ({ obj: filter && filter.rootCOAs, deep: true }),
  (_state, filter) => ({ obj: filter && filter.coas, deep: true }),
  (_state, filter) => ({ obj: filter && filter.isExcludedFromF2S, deep: false }),
  (_state, filter) => ({ obj: filter && filter.isScheduled, deep: false }),
  (
    transactionsById,
    { obj: accountIds },
    { obj: accountIdsArray },
    { obj: startLocalDate },
    { obj: endLocalDate },
    { obj: payee },
    { obj: amountSign },
    { obj: rootCOAs },
    { obj: coas },
    { obj: isExcludedFromF2S },
    { obj: isScheduled },
  ) => transactionsById && transactionsById.filter((transaction) => {
    let keepTransaction = true;

    keepTransaction = (keepTransaction && payee !== undefined ) ? transaction.payee === payee : keepTransaction;
    keepTransaction = (keepTransaction && amountSign !== undefined ) ? Math.sign(transaction.amount) === Math.sign(amountSign) : keepTransaction;
    keepTransaction = (keepTransaction && isScheduled !== undefined ) ? Boolean(transaction.stModelId) === Boolean(isScheduled) : keepTransaction;
    keepTransaction = (keepTransaction && isExcludedFromF2S !== undefined ) ? Boolean(transaction.isExcludedFromF2S) === Boolean(isExcludedFromF2S) : keepTransaction;
    keepTransaction = (keepTransaction && accountIds && accountIds.size > 0) ? accountIds.has(transaction.accountId) : keepTransaction;
    keepTransaction = (keepTransaction && accountIdsArray && accountIdsArray.size > 0) ?
      accountIdsArray.includes(transaction.accountId) : keepTransaction;
    const postedOnLocalDate = keepTransaction && DateTime.fromISO(transaction.postedOn).toJSDate();
    keepTransaction = (keepTransaction && startLocalDate) ? postedOnLocalDate >= startLocalDate : keepTransaction;
    keepTransaction = (keepTransaction && endLocalDate) ? postedOnLocalDate <= endLocalDate : keepTransaction;
    keepTransaction = (keepTransaction && rootCOAs && rootCOAs.size > 0) ? Boolean(
      (transaction.coa && rootCOAs.find((coa) => coa.type === transaction.coa.type && coa.id === transaction.coa.id)),
    ) : keepTransaction;
    keepTransaction = coas && !coas.size ? false : keepTransaction
    keepTransaction = (keepTransaction && coas && coas.size > 0) ? Boolean(
      (transaction.coa && coas.find((coa) => coa.type === transaction.coa.type && coa.id === transaction.coa.id))
      ||
      (transaction.split && transaction.split.items &&
        transaction.split.items.find((splitItem) => coas.find((coa) => coa.type === splitItem.coa.type && coa.id === splitItem.coa.id))
      )
    ) : keepTransaction;

    return keepTransaction;
  })
)(
  (state, filter) => filter && `${filter.isExcludedFromF2S}:${filter.isScheduled}:${filter.amountSign}:${filter.payee}
    :${JSON.stringify(filter.accountIdsArray)}:${filter.accountIds && filter.accountIds.hashCode()}
    :${filter.rootCOAs && filter.rootCOAs.hashCode()}:${filter.coas && filter.coas.hashCode && filter.coas.hashCode()}:${filter.startDate}:${filter.endDate}`,
  {
    cacheObject: new LruCacheObject({ cacheSize: 13 }),
    selectorCreator: createFlexEqualSelector,
  },
);

export const getTransactionsByTextFilter = createSelector(
  (state, props) => props.transactions || getTransactionsForAccountIds(state,props), // getTransactionsById(state, props),
  getAccountsById,
  getTags,
  (state, props) => props.filter,
  (transactionsById, accountsById, tagsById, filter) => {
    const filterLowerCase = filter && filter.toLowerCase();
    const filterDate = parseDateString(filter);
    let filterNumber = Number(filter);
    filterNumber = Number.isNaN(filterNumber) ? undefined : Math.round(Math.abs(filterNumber));

    const transactionsByIdFiltered = (!filterLowerCase || !filterLowerCase.length) ?
      transactionsById :
      transactionsById.filter((transaction) => {
        let keepTransaction = false;

        if (!keepTransaction && transaction.payee && transaction.payee.toLowerCase().includes(filterLowerCase)) {
          keepTransaction = true;
        }
        if (!keepTransaction) {
          const account = accountsById.get(transaction.accountId);
          if (account && account.name.toLowerCase().includes(filterLowerCase)) {
            keepTransaction = true;
          }
        }
        if (!keepTransaction && searchItemForText(transaction, filterLowerCase, tagsById, filterNumber)) {
          keepTransaction = true;
        }
        if (!keepTransaction && transaction.split && transaction.split.items) {
          if (transaction.split.items.find((item) => searchItemForText(item, filterLowerCase, tagsById))) {
            keepTransaction = true;
          }
        }
        if (!keepTransaction && filterDate && filterDate === parseDateString(transaction.postedOn)) {
          keepTransaction = true;
        }

        return keepTransaction;
      });

    return transactionsByIdFiltered;
  }
);

const searchItemForText = (item, filterText, tagsById, filterNumber) => {
  let found;

  if (!found && item.memo && item.memo.toLowerCase().includes(filterText)) {
    found = true;
  }
  if (!found && filterNumber !== undefined && Math.round(Math.abs(item.amount)) === filterNumber) {
    found = true;
  }
  if (!found) {
    const coaString = getCoaString(item.coa, true);
    if (coaString && coaString.toLowerCase().includes(filterText)) {
      found = true;
    }
  }
  if (!found && item.tags && tagsById) {
    if (item.tags.find((tagRef) => {
      const tag = tagsById.get(tagRef.id);
      return tag && tag.name.toLowerCase().includes(filterText);
    })) {
      found = true;
    }
  }

  return found;
};

export const getLastSyncDate = (state) => state[REDUCER_KEY].lastSyncDate;

export const findTransactionInAccountOrByTxnId = createCachedSelector(
  getTransactionsByAccountId,
  (state, props) => props.accountId,
  (state, props) => props.txnId,
  (transactionsByAccountId, accountId, txnId) =>
    findTransaction(transactionsByAccountId, txnId, accountId)
)(
  (state, props) => `${props.accountId}_${props.txnId}`,
  {
    cacheObject: new LruCacheObject({ cacheSize: 200 }),
  },
);

export const getTransactionsByAccountIdAndTxnId = (state, txnList) =>
  txnList.map((txn) => findTransactionInAccountOrByTxnId(state, { accountId: txn.accountId, txnId: txn.id }));

// getTransactionsByAccountIdsSelectors
//
// We are using a selector based on accounts to return a map of selectors that
// can then be used to get transactions for a specific account. This map of
// selectors are stored in a global cache based on the current login.
//
// We are using this level of indirection and a global cache so that each account
// will have it's own selector for the retrieval of transactions which will not
// be invalided by the adding/removing of accounts, the updating of the account
// or changing transaction data within the account.
//
// The selectors returned by this method are not typical selectors. Instead of
// a state and props, the expected arguments are a map of transactions by
// account ID and options map. Currently the only supported option are
// sortBy and sortOrder.
//
export const getTransactionsByAccountIdsSelectors = createSelector(
  getAccountsById,
  (accounts) => {
    let transactionsByAccountIdsSelector = getGlobalCache('TRANSACTIONS_SELECTORS');
    if (!transactionsByAccountIdsSelector) {
      setGlobalCache('TRANSACTIONS_SELECTORS', transactionsByAccountIdsSelector = new Map());
    }
    const activeAccountIds = [];
    accounts.forEach((account) => {
      activeAccountIds.push(account.id);

      // if selector doesn't exist, add it
      if (!transactionsByAccountIdsSelector.get(account.id)) {
        const selector = createSelector(
          (transactionsByAccountId) => transactionsByAccountId.get(account.id),
          (transactionsByAccountId, options) => options,
          (txns) => {   // was (txns, options) if you need the options

            // handle special case of no transactions in account
            if (txns === undefined || txns.size === 0) {
              return new ImmutableList([]);
            }
            return new ImmutableList(txns.toList());
          }
        );

        transactionsByAccountIdsSelector.set(account.id, selector);
      }
    });
    return new ImmutableMap(transactionsByAccountIdsSelector);
  }
);

export const isIncomeTxn = createSelector(
  (state, txn) => txn,
  getIncomeCOAIds,
  isIncomeFromTxn
);

